import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Tag, Typography, Row, Col, Pagination, Space, Spin, Tooltip, Rate, Button, Modal, Descriptions, message, Select, InputNumber, Checkbox, Dropdown, Badge } from 'antd';
import { CaretDownFilled, DeleteOutlined, LineChartOutlined, MinusOutlined, PlusOutlined, SwapOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { useDispatch } from 'react-redux';
import { bulkDeleteListings, fetchBestSalesPeriods, fetchGroupedSkuChildren, fetchGroupedSkus, fetchListingChart, handlePaginationChange, previewBulkDeleteListings } from '../actions';
import { setFilters } from '../reducers';
import ProductImagesGallery from '../../../components/ProductImagesGallery/ProductImagesGallery';
import './Listings.css';

const { Text, Paragraph } = Typography;

const COLUMN_WIDTHS = {
  selection: 48,
  hierarchy: 80,
  variation: 120,
  rating: 140,
  rank: 170,
  sale: 100,
  salesPeriod: 120,
  bestPeriod: 170,
  charts: 80,
  seller: 120,
  channel: 90,
  status: 140
};

const emptyCell = <span style={{ color: '#999' }}>-</span>;

const ListingsList = ({
  listings,
  isLoading,
  pagination,
  filters,
  expandedRowKeys,
  onExpandedRowsChange,
  onReload
}) => {
  const dispatch = useDispatch();
  const [groupedSkuData, setGroupedSkuData] = useState({});
  const [leafSkuData, setLeafSkuData] = useState({});
  const [loadingGroups, setLoadingGroups] = useState({});
  const [loadingLeafSkus, setLoadingLeafSkus] = useState({});
  const [expandedGroupKeys, setExpandedGroupKeys] = useState([]);

  const [isChartModalVisible, setIsChartModalVisible] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [chartTitle, setChartTitle] = useState('');
  const [chartDays, setChartDays] = useState(30);
  const [chartScope, setChartScope] = useState(null);
  const [chartVisibleMetrics, setChartVisibleMetrics] = useState(['sales', 'rank', 'price']);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedGroupRowKeys, setSelectedGroupRowKeys] = useState([]);
  const [selectedGroupRows, setSelectedGroupRows] = useState([]);
  const [selectedSkuRowKeys, setSelectedSkuRowKeys] = useState([]);
  const [selectedSkuRows, setSelectedSkuRows] = useState([]);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkPayload, setBulkPayload] = useState(null);
  const [filterBulkModes, setFilterBulkModes] = useState(['listing']);
  const [bestPeriodConfig, setBestPeriodConfig] = useState({ totalPeriod: 30, windowSize: 5 });
  const [bestPeriodByScope, setBestPeriodByScope] = useState({});
  const [bestPeriodLoading, setBestPeriodLoading] = useState(false);
  const [bestPeriodApplied, setBestPeriodApplied] = useState(false);

  const salesPeriodDays = Number(filters?.salesPeriod || 30);
  const salesPeriodLabel = salesPeriodDays === 3650 ? 'All time' : `${salesPeriodDays} days`;
  const getSortOrder = (key, prefix = '') => {
    const sortBy = prefix ? filters?.[`${prefix}SortBy`] : filters?.sortBy;
    const sortOrder = prefix ? filters?.[`${prefix}SortOrder`] : filters?.sortOrder;
    if (sortBy !== key) return null;
    return String(sortOrder).toUpperCase() === 'ASC' ? 'ascend' : 'descend';
  };
  const renderSalesPeriodValue = (value, record) => (
    <div style={{ textAlign: 'center' }}>
      <strong style={{ color: '#18a058' }}>{Number(value ?? record.salesPeriodCount ?? 0).toLocaleString()}</strong>
      <div style={{ fontSize: 10, color: '#999' }}>{salesPeriodLabel}</div>
    </div>
  );
  const getNumericSortValue = (record, field) => {
    const source = record?.representativeItem || record || {};
    return Number(record?.[field] ?? source?.[field] ?? 0) || 0;
  };

  const getStatusTag = (status) => {
    const config = {
      ACTIVE: ['green', 'Active'],
      INACTIVE: ['orange', 'Inactive'],
      DELETED: ['red', 'Deleted']
    };
    const [color, text] = config[status] || ['default', status || '-'];
    return <Tag style={{ width: 110, textAlign: 'center' }} color={color}>{text}</Tag>;
  };

  const getFulfillmentChannelTag = (channel) => {
    if (channel === 'FBA') return <Tag color="orange" style={{ textAlign: 'center', fontSize: 12, width: 43, marginRight: 0 }}>FBA</Tag>;
    if (channel === 'FBM') return <Tag color="blue" style={{ textAlign: 'center', fontSize: 12, width: 43, marginRight: 0 }}>FBM</Tag>;
    return <Tag color="default" style={{ textAlign: 'center', fontSize: 12 }}>{channel || '-'}</Tag>;
  };

  const getSellerAccountText = (record) => record?.sellerAccount?.name || 'Unknown';

  const renderRating = (record) => {
    const rating = record.rating || record.representativeItem?.rating;
    const reviewCount = record.reviewCount || record.representativeItem?.reviewCount;

    if (!rating && !reviewCount) {
      return <span style={{ color: '#999' }}>-</span>;
    }

    return (
      <div style={{ textAlign: 'center' }}>
        <Rate disabled allowHalf value={Number(rating) || 0} style={{ fontSize: 12, color: '#faad14' }} />
        <div style={{ fontSize: 10, color: '#999', marginTop: 1 }}>
          ({Number(reviewCount || 0).toLocaleString()} reviews)
        </div>
      </div>
    );
  };

  const renderRank = (record) => {
    const source = record.representativeItem || record;
    const mainRank = record.mainRank || source.mainRank;
    const mainRankName = record.mainRankName || source.mainRankName;
    const normalizedRank = record.normalizedRank || source.normalizedRank;

    if (!mainRank && !mainRankName && !normalizedRank) {
      return <span style={{ color: '#999' }}>-</span>;
    }

    return (
      <div className="listings-rank-cell">
        {mainRankName && (
          <div className="listings-rank-name" title={mainRankName}>
            {mainRankName}
          </div>
        )}
        {mainRank && <div style={{ fontWeight: 'bold' }}>#{Number(mainRank).toLocaleString()}</div>}
        {normalizedRank && (
          <div className="listings-normalized-rank">
            <SwapOutlined className="listings-normalized-rank-icon" />
            <span>#{Number(normalizedRank).toLocaleString()}</span>
          </div>
        )}
      </div>
    );
  };

  const resolveChartScope = (record) => {
    if (record.parentSku && record.groupedSku && record.sku) {
      return { parentSku: record.parentSku, level: 'leaf', sku: record.sku };
    }
    if (record.parentSku && record.groupedSku) {
      return { parentSku: record.parentSku, level: 'grouped', groupedSku: record.groupedSku };
    }
    return { parentSku: record.sku, level: 'parent' };
  };

  const loadChart = async (scope, days = chartDays) => {
    const result = await dispatch(fetchListingChart({ ...scope, days }));
    const salesByDate = new Map((result?.salesTrend || []).map(item => [item.date, item]));
    const rankByDate = new Map((result?.rankTrend || []).map(item => [item.date, item]));
    const priceByDate = new Map((result?.priceHistory || []).map(item => [item.date, item]));
    const dates = [...new Set([
      ...salesByDate.keys(),
      ...rankByDate.keys(),
      ...priceByDate.keys()
    ])].sort();

    setChartData(dates.map(dateValue => {
      const [, month, day] = dateValue.split('-').map(Number);
      const sales = salesByDate.get(dateValue);
      const rank = rankByDate.get(dateValue);
      const price = priceByDate.get(dateValue);
      return {
        date: `${month}/${day}`,
        fullDate: dateValue,
        sales: sales?.salesCount ?? 0,
        rank: rank?.mainRank ?? null,
        normalizedRank: rank?.normalizedRank ?? null,
        price: price?.price ?? null
      };
    }));
  };

  const openChart = async (record) => {
    const scope = resolveChartScope(record);
    setChartScope(scope);
    await loadChart(scope, chartDays);
    setChartTitle(`Sales / Rank / Price - ${record.sku || record.groupedSku}`);
    setIsChartModalVisible(true);
  };

  const handleChartDaysChange = async (days) => {
    setChartDays(days);
    if (chartScope) await loadChart(chartScope, days);
  };

  const handleTableChange = (tablePagination, _tableFilters, sorter) => {
    const activeSorter = Array.isArray(sorter) ? sorter.find(item => item.order) : sorter;
    const sortBy = activeSorter?.order ? activeSorter.columnKey : null;
    const sortOrder = activeSorter?.order === 'ascend' ? 'ASC' : activeSorter?.order === 'descend' ? 'DESC' : null;
    dispatch(setFilters({
      sortBy,
      sortOrder,
      sortDays: sortBy === 'salesPeriod' ? salesPeriodDays : null
    }));
    dispatch(handlePaginationChange(tablePagination.current || 1, tablePagination.pageSize || pagination.pageSize));
  };

  const selectedItemCount = selectedRowKeys.length + selectedGroupRowKeys.length + selectedSkuRowKeys.length;

  useEffect(() => {
    setGroupedSkuData({});
    setLeafSkuData({});
    setExpandedGroupKeys([]);
  }, [filters.groupSortBy, filters.groupSortOrder, filters.groupSortDays, filters.salesPeriod]);

  useEffect(() => {
    setGroupedSkuData({});
    setLeafSkuData({});
    setExpandedGroupKeys([]);
    setSelectedGroupRowKeys([]);
    setSelectedGroupRows([]);
    setSelectedSkuRowKeys([]);
    setSelectedSkuRows([]);
  }, [listings]);

  const getListingScopeKey = (record) => `listing:${record.sellerId || record.sellerAccount?.id || 'all'}:${record.sku}`;
  const getGroupScopeKey = (record) => `group:${record.id || `${record.parentSku}:${record.groupedSku}`}`;
  const getSkuScopeKey = (record) => `sku:${record.id || record.sku}`;

  const buildBestPeriodScopes = (extraScopes = []) => {
    const scopes = [];
    const seen = new Set();
    const pushScope = (scope) => {
      if (!scope?.key || seen.has(scope.key)) return;
      seen.add(scope.key);
      scopes.push(scope);
    };

    (listings || []).forEach(listing => {
      pushScope({
        key: getListingScopeKey(listing),
        type: 'listing',
        parentSku: listing.sku,
        sellerId: listing.sellerId || listing.sellerAccount?.id || null
      });
    });

    Object.values(groupedSkuData).flat().forEach(group => {
      pushScope({
        key: getGroupScopeKey(group),
        type: 'group',
        groupId: group.id,
        parentSku: group.parentSku,
        groupedSku: group.groupedSku
      });
    });

    Object.values(leafSkuData).flat().forEach(child => {
      pushScope({
        key: getSkuScopeKey(child),
        type: 'sku',
        skuItemId: child.id,
        sku: child.sku
      });
    });

    extraScopes.forEach(pushScope);
    return scopes;
  };

  const loadBestPeriods = async (extraScopes = []) => {
    const scopes = buildBestPeriodScopes(extraScopes);
    if (scopes.length === 0) return;
    setBestPeriodLoading(true);
    try {
      const result = await dispatch(fetchBestSalesPeriods({
        scopes,
        totalPeriod: bestPeriodConfig.totalPeriod,
        windowSize: bestPeriodConfig.windowSize
      }));
      const byScope = {};
      (result?.rows || []).forEach(row => {
        byScope[row.key] = row;
      });
      setBestPeriodByScope(prev => ({ ...prev, ...byScope }));
      setBestPeriodApplied(true);
    } finally {
      setBestPeriodLoading(false);
    }
  };

  const openBulkDelete = async (mode = 'listing') => {
    setBulkLoading(true);
    try {
      let payload;
      if (mode === 'filter') {
        payload = { mode: 'filter', modes: filterBulkModes, filters: buildFilterPayload(filters) };
      } else if (mode === 'selection') {
        payload = {
          mode: 'selection',
          parentSkus: selectedRows.map(row => row.sku).filter(Boolean),
          groupIds: selectedGroupRows.map(row => row.id).filter(Boolean),
          skuItemIds: selectedSkuRows.map(row => row.id).filter(Boolean)
        };
      } else if (mode === 'sku_item') {
        payload = {
          mode: 'sku_item',
          skuItemIds: selectedSkuRows.map(row => row.id).filter(Boolean)
        };
      } else {
        payload = { mode: 'listing', parentSkus: selectedRows.map(row => row.sku).filter(Boolean) };
      }
      if (payload.mode === 'listing' && payload.parentSkus.length === 0) return;
      if (payload.mode === 'sku_item' && payload.skuItemIds.length === 0) return;
      if (payload.mode === 'selection' && payload.parentSkus.length === 0 && payload.groupIds.length === 0 && payload.skuItemIds.length === 0) return;
      if (payload.mode === 'filter' && (!payload.modes || payload.modes.length === 0)) return;
      const preview = await dispatch(previewBulkDeleteListings(payload));
      setBulkPreview(preview);
      setBulkPayload(payload);
      setBulkModalOpen(true);
    } catch (error) {
      message.error(error.response?.data?.message || error.message || 'Cannot preview bulk delete');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleFilterBulkModesChange = async (modes) => {
    const nextModes = modes.length > 0 ? modes : ['listing'];
    setFilterBulkModes(nextModes);
    if (!bulkPayload || bulkPayload.mode !== 'filter') return;

    setBulkLoading(true);
    try {
      const nextPayload = {
        ...bulkPayload,
        modes: nextModes,
        filters: buildFilterPayload(filters)
      };
      const preview = await dispatch(previewBulkDeleteListings(nextPayload));
      setBulkPayload(nextPayload);
      setBulkPreview(preview);
    } catch (error) {
      message.error(error.response?.data?.message || error.message || 'Cannot preview bulk delete');
    } finally {
      setBulkLoading(false);
    }
  };

  const confirmBulkDelete = async () => {
    if (!bulkPreview) return;
    setBulkLoading(true);
    try {
      await dispatch(bulkDeleteListings(bulkPayload || {
        mode: 'listing',
        parentSkus: bulkPreview.parentSkus || []
      }));
      message.success(`Queued Amazon delete and marked ${bulkPreview.skuCount || 0} SKU items as deleted`);
      setSelectedRowKeys([]);
      setSelectedRows([]);
      setSelectedGroupRowKeys([]);
      setSelectedGroupRows([]);
      setSelectedSkuRowKeys([]);
      setSelectedSkuRows([]);
      setBulkPreview(null);
      setBulkPayload(null);
      setBulkModalOpen(false);
      if (onReload) onReload();
    } catch (error) {
      message.error(error.response?.data?.message || error.message || 'Cannot bulk delete listings');
    } finally {
      setBulkLoading(false);
    }
  };

  const bulkActionMenuItems = useMemo(() => ([
    {
      key: 'deleteSelected',
      label: 'Delete selected listings / SKUs',
      icon: <DeleteOutlined />,
      danger: true,
      disabled: selectedItemCount === 0
    }
  ]), [selectedItemCount]);

  const handleBulkActionMenuClick = useCallback(({ key }) => {
    if (key === 'deleteSelected') {
      openBulkDelete('selection');
    }
  }, [openBulkDelete]);

  const handleParentExpand = async (expanded, record) => {
    if (!expanded) return;
    setLoadingGroups(prev => ({ ...prev, [record.sku]: true }));
    try {
      const groups = await dispatch(fetchGroupedSkus(record.sku, filters));
      const groupsWithParent = (groups || []).map(group => ({ ...group, parentSku: record.sku }));
      const groupIds = new Set(groupsWithParent.map(group => group.id).filter(Boolean));
      setGroupedSkuData(prev => ({ ...prev, [record.sku]: groupsWithParent }));
      setLeafSkuData(prev => Object.fromEntries(
        Object.entries(prev).filter(([key]) => !key.startsWith(`${record.sku}::`))
      ));
      setExpandedGroupKeys(prev => prev.filter(key => groupIds.has(key)));
      if (bestPeriodApplied) {
        await loadBestPeriods(groupsWithParent.map(group => ({
          key: getGroupScopeKey(group),
          type: 'group',
          groupId: group.id,
          parentSku: group.parentSku,
          groupedSku: group.groupedSku
        })));
      }
    } finally {
      setLoadingGroups(prev => ({ ...prev, [record.sku]: false }));
    }
  };

  const handleGroupExpand = async (expanded, groupRecord) => {
    const key = `${groupRecord.parentSku}::${groupRecord.groupedSku}`;
    if (!expanded || leafSkuData[key]) return;
    setLoadingLeafSkus(prev => ({ ...prev, [key]: true }));
    try {
      const children = await dispatch(fetchGroupedSkuChildren(groupRecord.parentSku, groupRecord.groupedSku, filters));
      const childrenWithScope = (children || []).map(child => ({
        ...child,
        parentSku: groupRecord.parentSku,
        groupedSku: groupRecord.groupedSku
      }));
      setLeafSkuData(prev => ({ ...prev, [key]: childrenWithScope }));
      if (bestPeriodApplied) {
        await loadBestPeriods(childrenWithScope.map(child => ({
          key: getSkuScopeKey(child),
          type: 'sku',
          skuItemId: child.id,
          sku: child.sku
        })));
      }
    } finally {
      setLoadingLeafSkus(prev => ({ ...prev, [key]: false }));
    }
  };

  const isParentExpandable = (record) => (record.groupedSkuCount ?? record.childrenCount ?? 0) > 0;
  const isGroupExpandable = (record) => (record.childCount ?? 0) > 0;

  const toggleParentRow = async (record) => {
    if (!isParentExpandable(record)) return;
    const rowKey = record.key || record.id;
    const currentKeys = expandedRowKeys || [];
    const isExpanded = currentKeys.includes(rowKey);
    if (!isExpanded) await handleParentExpand(true, record);
    if (onExpandedRowsChange) {
      onExpandedRowsChange(isExpanded
        ? currentKeys.filter(key => key !== rowKey)
        : [...currentKeys, rowKey]);
    }
  };

  const toggleGroupRow = async (record) => {
    if (!isGroupExpandable(record)) return;
    const rowKey = record.key || record.id;
    const isExpanded = expandedGroupKeys.includes(rowKey);
    if (!isExpanded) await handleGroupExpand(true, record);
    setExpandedGroupKeys(prev => (
      isExpanded
        ? prev.filter(key => key !== rowKey)
        : [...prev.filter(key => key !== rowKey), rowKey]
    ));
  };

  const renderHierarchyCell = ({ expanded, expandable, loading = false, onToggle }) => {
    if (!expandable) return null;

    return (
      <div className="listings-hierarchy-cell">
        <Button
          size="small"
          type="text"
          className="listings-expand-button"
          loading={loading}
          icon={!loading && (expanded ? <MinusOutlined /> : <PlusOutlined />)}
          onClick={(event) => {
            event.stopPropagation();
            if (loading) return;
            onToggle();
          }}
        />
      </div>
    );
  };

  const renderVariationCount = ({ count, label = 'variations', expandable, onToggle }) => {
    const value = Number(count || 0);
    if (!value) return emptyCell;

    return (
      <Button
        type="link"
        size="small"
        disabled={!expandable}
        className="listings-variation-count"
        onClick={(event) => {
          event.stopPropagation();
          if (expandable) onToggle();
        }}
      >
        {value.toLocaleString()} {label}
      </Button>
    );
  };

  const buildFilterPayload = (source = {}) => ({
    searchText: source.searchText || '',
    searchScope: source.searchScope || 'all',
    fulfillmentChannel: source.selectedFulfillmentChannel || source.fulfillmentChannel || 'all',
    status: source.selectedStatus || source.status || 'all',
    sellerAccountId: source.selectedSellerAccount || source.sellerAccountId || 'all',
    compoundFilters: source.compoundFilters || [],
    sortBy: source.sortBy,
    sortOrder: source.sortOrder,
    sortDays: source.sortDays,
    groupSortBy: source.groupSortBy,
    groupSortOrder: source.groupSortOrder,
    groupSortDays: source.groupSortDays,
    salesPeriod: source.salesPeriod
  });

  const renderBestPeriod = (scopeKey) => {
    if (bestPeriodLoading) return <Spin size="small" />;
    const best = bestPeriodByScope[scopeKey];
    if (!best) return emptyCell;
    const fmtDate = (d) => d ? d.split('-').reverse().join('/') : '';
    return (
      <div>
        <div style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>{fmtDate(best.dateStart)} - {fmtDate(best.dateEnd)}</div>
        <span style={{ color: '#1677ff', fontWeight: 500 }}>{best.windowTotal || 0} sales</span>
      </div>
    );
  };

  const getRowClassName = useCallback((record, index, level = 'parent') => {
    return `listing-${level}-row ${index % 2 === 0 ? 'table-row-even' : 'table-row-odd'}`;
  }, []);

  const skuCell = (record, options = {}) => {
    const imageSource = record.representativeItem || record;
    const source = options.useRepresentativeText === false ? record : imageSource;
    const sku = options.useGroupedSku ? (record.groupedSku || record.sku) : (record.sku || record.groupedSku);
    const title = source.title || record.title || sku;
    const asin = source.asin || record.asin;
    const imageItem = imageSource.images ? imageSource : record;
    const levelClassName = options.levelLabel
      ? ` listings-sku-cell-${options.levelLabel.toLowerCase()}`
      : '';

    return (
      <Row className={`listings-sku-cell${levelClassName}`} style={{ flexFlow: 'nowrap' }} align="middle" gutter={8}>
        <Col flex="none">
          <ProductImagesGallery skuItem={imageItem} maxVisibleImages={1} showRemainingCount={false} imageSize={options.imageSize || 42} />
        </Col>
        <Col flex="auto" className="listings-sku-text">
          <div className="listings-sku-title-line">
            {options.levelLabel && <span className={`listings-level-badge listings-level-${options.levelLabel.toLowerCase()}`}>{options.levelLabel}</span>}
            <Text className="listings-sku-title">{title}</Text>
          </div>
          <Paragraph style={{ marginBottom: 0, fontSize: 12 }} ellipsis={{ rows: 1 }} className="no-line-through">
            <b>ASIN:</b>{' '}
            {asin ? (
              <a target="_blank" rel="noreferrer" className="no-line-through" href={`https://www.amazon.com/dp/${asin}`}>
                {asin}
              </a>
            ) : '-'}
          </Paragraph>
          <Paragraph style={{ marginBottom: 0, fontSize: 12 }} ellipsis={{ rows: 1 }} className="no-line-through">
            <b>SKU:</b> {sku || '-'}
          </Paragraph>
        </Col>
      </Row>
    );
  };

  const chartColumn = {
    title: 'Charts',
    key: 'charts',
    width: COLUMN_WIDTHS.charts,
    align: 'center',
    render: (_, record) => (
      <Space size={5}>
        <Button size="small" icon={<LineChartOutlined />} onClick={() => openChart(record)} title="Sales, Rank, Price Trend" />
      </Space>
    )
  };

  const bestPeriodPlaceholderColumn = {
    title: 'Best Period',
    key: 'bestPeriod',
    width: COLUMN_WIDTHS.bestPeriod,
    render: (_, record) => {
      if (record?.groupedSku) return renderBestPeriod(getGroupScopeKey(record));
      return renderBestPeriod(getListingScopeKey(record));
    }
  };

  const selectionPlaceholderColumn = {
    title: '',
    key: 'selectionPlaceholder',
    width: COLUMN_WIDTHS.selection,
    render: () => null
  };

  const hierarchyPlaceholderColumn = {
    title: '',
    key: 'hierarchyPlaceholder',
    width: COLUMN_WIDTHS.hierarchy,
    render: () => null
  };

  const variationPlaceholderColumn = {
    title: 'Variation',
    key: 'variation',
    width: COLUMN_WIDTHS.variation,
    align: 'center',
    render: () => emptyCell
  };

  const sellerPlaceholderColumn = {
    title: 'Seller Account',
    key: 'sellerAccount',
    width: COLUMN_WIDTHS.seller,
    render: () => emptyCell
  };

  const channelPlaceholderColumn = {
    title: 'Channel',
    key: 'fulfillmentChannel',
    width: COLUMN_WIDTHS.channel,
    align: 'center',
    render: () => emptyCell
  };

  const statusPlaceholderColumn = {
    title: 'Status',
    key: 'status',
    width: COLUMN_WIDTHS.status,
    align: 'center',
    render: () => emptyCell
  };

  const leafColumns = [
    hierarchyPlaceholderColumn,
    {
      title: <Tooltip title="Mã SKU">SKU / ASIN</Tooltip>,
      dataIndex: 'sku',
      key: 'sku',
      ellipsis: true,
      render: (_, record) => skuCell(record, { imageSize: 49, levelLabel: 'SKU' })
    },
    variationPlaceholderColumn,
    { title: 'Rating', key: 'rating', width: COLUMN_WIDTHS.rating, align: 'center', sorter: (a, b) => getNumericSortValue(a, 'rating') - getNumericSortValue(b, 'rating'), render: (_, record) => renderRating(record) },
    { title: '#Best Sellers Rank', key: 'mainRank', width: COLUMN_WIDTHS.rank, sorter: (a, b) => getNumericSortValue(a, 'mainRank') - getNumericSortValue(b, 'mainRank'), render: (_, record) => renderRank(record) },
    {
      title: 'Today Sales',
      dataIndex: 'salesCount',
      key: 'salesCount',
      width: COLUMN_WIDTHS.sale,
      align: 'center',
      sorter: (a, b) => getNumericSortValue(a, 'salesCount') - getNumericSortValue(b, 'salesCount'),
      render: (value, record) => <strong style={{ color: '#1677ff' }}>{value ?? record.quantityOrdered ?? 0}</strong>
    },
    {
      title: 'Sales Period',
      dataIndex: 'salesPeriodCount',
      key: 'salesPeriod',
      width: COLUMN_WIDTHS.salesPeriod,
      align: 'center',
      sorter: (a, b) => getNumericSortValue(a, 'salesPeriodCount') - getNumericSortValue(b, 'salesPeriodCount'),
      render: renderSalesPeriodValue
    },
    {
      title: 'Best Period',
      key: 'bestPeriod',
      width: COLUMN_WIDTHS.bestPeriod,
      render: (_, record) => renderBestPeriod(getSkuScopeKey(record))
    },
    chartColumn,
    {
      title: 'Seller Account',
      dataIndex: 'sellerAccount',
      key: 'sellerAccount',
      width: COLUMN_WIDTHS.seller,
      render: (_, record) => <span className="no-line-through">{getSellerAccountText(record)}</span>
    },
    {
      title: 'Channel',
      dataIndex: 'fulfillmentChannel',
      key: 'fulfillmentChannel',
      width: COLUMN_WIDTHS.channel,
      align: 'center',
      render: (channel) => <span className="no-line-through">{getFulfillmentChannelTag(channel)}</span>
    },
    { title: 'Status', dataIndex: 'status', key: 'status', width: COLUMN_WIDTHS.status, align: 'center', render: (status) => getStatusTag(status) }
  ];

  const groupColumns = [
    {
      title: 'SKUs',
      dataIndex: 'childCount',
      key: 'childCount',
      width: COLUMN_WIDTHS.hierarchy,
      align: 'center',
      sorter: (a, b) => getNumericSortValue(a, 'childCount') - getNumericSortValue(b, 'childCount'),
      render: (count, record) => renderHierarchyCell({
        expanded: expandedGroupKeys.includes(record.key || record.id),
        expandable: isGroupExpandable(record),
        loading: !!loadingLeafSkus[`${record.parentSku}::${record.groupedSku}`],
        onToggle: () => toggleGroupRow(record)
      })
    },
    {
      title: <Tooltip title="Grouped SKU">Grouped SKU / ASIN</Tooltip>,
      dataIndex: 'groupedSku',
      key: 'groupedSku',
      ellipsis: true,
      render: (_, record) => skuCell(record, { imageSize: 49, levelLabel: 'GROUP', useGroupedSku: true })
    },
    {
      title: 'Variation',
      key: 'variation',
      width: COLUMN_WIDTHS.variation,
      align: 'center',
      render: (_, record) => renderVariationCount({
        count: record.childCount,
        label: 'SKUs',
        expandable: isGroupExpandable(record),
        onToggle: () => toggleGroupRow(record)
      })
    },
    { title: 'Rating', key: 'rating', width: COLUMN_WIDTHS.rating, align: 'center', sorter: (a, b) => getNumericSortValue(a, 'rating') - getNumericSortValue(b, 'rating'), render: (_, record) => renderRating(record) },
    { title: '#Best Sellers Rank', key: 'mainRank', width: COLUMN_WIDTHS.rank, sorter: (a, b) => getNumericSortValue(a, 'mainRank') - getNumericSortValue(b, 'mainRank'), render: (_, record) => renderRank(record) },
    {
      title: 'Today Sales',
      dataIndex: 'salesCount',
      key: 'salesCount',
      width: COLUMN_WIDTHS.sale,
      align: 'center',
      sorter: (a, b) => getNumericSortValue(a, 'salesCount') - getNumericSortValue(b, 'salesCount'),
      render: (value) => <strong style={{ fontSize: 13, color: '#1677ff' }}>{value || 0}</strong>
    },
    {
      title: 'Sales Period',
      dataIndex: 'salesPeriodCount',
      key: 'salesPeriod',
      width: COLUMN_WIDTHS.salesPeriod,
      align: 'center',
      sorter: (a, b) => getNumericSortValue(a, 'salesPeriodCount') - getNumericSortValue(b, 'salesPeriodCount'),
      render: renderSalesPeriodValue
    },
    bestPeriodPlaceholderColumn,
    chartColumn,
    sellerPlaceholderColumn,
    channelPlaceholderColumn,
    statusPlaceholderColumn
  ];

  const parentColumns = [
    {
      title: 'Groups',
      dataIndex: 'groupedSkuCount',
      key: 'groupedSkuCount',
      width: COLUMN_WIDTHS.hierarchy,
      align: 'center',
      sorter: true,
      sortOrder: getSortOrder('groupedSkuCount'),
      render: (count, record) => renderHierarchyCell({
        expanded: (expandedRowKeys || []).includes(record.key || record.id),
        expandable: isParentExpandable(record),
        loading: !!loadingGroups[record.sku],
        onToggle: () => toggleParentRow(record)
      })
    },
    {
      title: <Tooltip title="Mã SKU">SKU / ASIN</Tooltip>,
      dataIndex: 'sku',
      key: 'sku',
      ellipsis: true,
      render: (_, record) => skuCell(record, { imageSize: 49, useRepresentativeText: false, levelLabel: 'LISTING' })
    },
    {
      title: 'Variation',
      key: 'variation',
      width: COLUMN_WIDTHS.variation,
      align: 'center',
      render: (_, record) => renderVariationCount({
        count: record.groupedSkuCount ?? record.childrenCount,
        label: 'groups',
        expandable: isParentExpandable(record),
        onToggle: () => toggleParentRow(record)
      })
    },
    { title: 'Rating', key: 'rating', width: COLUMN_WIDTHS.rating, align: 'center', sorter: true, sortOrder: getSortOrder('rating'), render: (_, record) => renderRating(record) },
    { title: '#Best Sellers Rank', key: 'mainRank', width: COLUMN_WIDTHS.rank, sorter: true, sortOrder: getSortOrder('mainRank'), render: (_, record) => renderRank(record) },
    {
      title: 'Today Sales',
      dataIndex: 'salesCount',
      key: 'salesCount',
      width: COLUMN_WIDTHS.sale,
      align: 'center',
      sorter: true,
      sortOrder: getSortOrder('salesCount'),
      render: (value, record) => <strong style={{ color: '#1677ff' }}>{value ?? record.quantityOrdered ?? 0}</strong>
    },
    {
      title: 'Sales Period',
      dataIndex: 'salesPeriodCount',
      key: 'salesPeriod',
      width: COLUMN_WIDTHS.salesPeriod,
      align: 'center',
      sorter: true,
      sortOrder: getSortOrder('salesPeriod'),
      render: renderSalesPeriodValue
    },
    bestPeriodPlaceholderColumn,
    chartColumn,
    {
      title: 'Seller Account',
      dataIndex: 'sellerAccount',
      key: 'sellerAccount',
      width: COLUMN_WIDTHS.seller,
      render: (_, record) => <span className="no-line-through">{getSellerAccountText(record)}</span>
    },
    channelPlaceholderColumn,
    { title: 'Status', dataIndex: 'status', key: 'status', width: COLUMN_WIDTHS.status, align: 'center', render: (status) => getStatusTag(status) }
  ];

  const expandedGroupRowRender = (groupRecord) => {
    const key = `${groupRecord.parentSku}::${groupRecord.groupedSku}`;
    const children = leafSkuData[key] || [];
    const loading = loadingLeafSkus[key];
    const childIds = children.map(child => child.id).filter(Boolean);

    if (loading) {
      return <Spin style={{ display: 'block', textAlign: 'center', padding: 20 }} />;
    }

    return (
      <div className="listing-leaf-level">
        <div className="listing-nested-toolbar">
          <Text strong className="listing-level-label">SKU variants</Text>
        </div>
        <Table
          className="listing-leaf-table"
          bordered={false}
          rowKey="id"
          childrenColumnName="__tableChildren"
          columns={leafColumns}
          dataSource={children.map(child => ({
            ...child,
            key: child.id,
            parentSku: groupRecord.parentSku,
            groupedSku: groupRecord.groupedSku
          }))}
          pagination={false}
          rowSelection={{
            columnWidth: COLUMN_WIDTHS.selection,
            selectedRowKeys: selectedSkuRowKeys.filter(rowKey => childIds.includes(rowKey)),
            onChange: (keys, rows) => {
              setSelectedSkuRowKeys(prev => [
                ...prev.filter(rowKey => !childIds.includes(rowKey)),
                ...keys
              ]);
              setSelectedSkuRows(prev => [
                ...prev.filter(row => !childIds.includes(row.id)),
                ...rows
              ]);
            },
            getCheckboxProps: (record) => ({
              disabled: record.status === 'DELETED'
            })
          }}
          rowClassName={(child, index) => getRowClassName(child, index, 'leaf')}
          showHeader={false}
          size="small"
          tableLayout="fixed"
        />
      </div>
    );
  };

  const expandedParentRowRender = (record) => {
    const groups = groupedSkuData[record.sku] || [];
    const loading = loadingGroups[record.sku];
    const groupIds = groups.map(group => group.id).filter(Boolean);
    const validGroupIds = new Set(groupIds);
    const safeExpandedGroupKeys = expandedGroupKeys.filter(key => validGroupIds.has(key));

    if (loading) {
      return <Spin style={{ display: 'block', textAlign: 'center', padding: 20 }} />;
    }

    return (
      <div className="listing-group-level">
        <div className="listing-level-label">Grouped variants</div>
        <Table
          className="listing-group-table"
          bordered={false}
          rowKey="id"
          childrenColumnName="__tableChildren"
          columns={groupColumns}
          dataSource={groups.map(group => ({ ...group, key: group.id, parentSku: record.sku }))}
          pagination={false}
          rowSelection={{
            columnWidth: COLUMN_WIDTHS.selection,
            selectedRowKeys: selectedGroupRowKeys.filter(rowKey => groupIds.includes(rowKey)),
            onChange: (keys, rows) => {
              setSelectedGroupRowKeys(prev => [
                ...prev.filter(rowKey => !groupIds.includes(rowKey)),
                ...keys
              ]);
              setSelectedGroupRows(prev => [
                ...prev.filter(row => !groupIds.includes(row.id)),
                ...rows
              ]);
            }
          }}
          rowClassName={(group, index) => getRowClassName(group, index, 'group')}
          showHeader={false}
          size="small"
          tableLayout="fixed"
          expandable={{
            expandedRowKeys: safeExpandedGroupKeys,
            onExpandedRowsChange: keys => setExpandedGroupKeys(keys.filter(key => validGroupIds.has(key))),
            onExpand: handleGroupExpand,
            expandedRowRender: expandedGroupRowRender,
            rowExpandable: isGroupExpandable,
            showExpandColumn: false,
            expandRowByClick: false
          }}
        />
      </div>
    );
  };

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginTop: 13, marginBottom: 10 }}>
        <Col>
          <Space direction="horizontal" size={4}>
            <Text strong style={{ fontSize: 14 }}>Total Listings:</Text>
            <Pagination
              size="small"
              current={pagination?.current || 1}
              total={pagination?.total || 0}
              pageSize={pagination?.pageSize || 25}
              onChange={(page, pageSize) => dispatch(handlePaginationChange(page, pageSize))}
              showSizeChanger={false}
              showQuickJumper={false}
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
            />
          </Space>
        </Col>
        <Col>
          <Space size={8} wrap>
            <Text type="secondary">Best period</Text>
            <Select
              size="small"
              value={bestPeriodConfig.totalPeriod}
              style={{ width: 120 }}
              onChange={(value) => {
                setBestPeriodConfig(prev => ({ ...prev, totalPeriod: value, windowSize: Math.min(prev.windowSize, value) }));
                setBestPeriodByScope({});
                setBestPeriodApplied(false);
              }}
              options={[7, 14, 30, 60, 90].map(value => ({ value, label: `Last ${value}d` }))}
            />
            <InputNumber
              size="small"
              min={1}
              max={bestPeriodConfig.totalPeriod}
              value={bestPeriodConfig.windowSize}
              onChange={(value) => {
                setBestPeriodConfig(prev => ({ ...prev, windowSize: value || 1 }));
                setBestPeriodByScope({});
                setBestPeriodApplied(false);
              }}
              style={{ width: 80 }}
            />
            <Button size="small" onClick={() => loadBestPeriods()} loading={bestPeriodLoading}>
              Apply
            </Button>
            <Button size="small" danger icon={<DeleteOutlined />} loading={bulkLoading} onClick={() => openBulkDelete('filter')}>
              Delete by Filter
            </Button>
            {selectedItemCount > 0 && (
              <Badge count={selectedItemCount} offset={[-8, -5]} showZero={false}>
                <Dropdown
                  menu={{
                    items: bulkActionMenuItems,
                    onClick: handleBulkActionMenuClick
                  }}
                  trigger={['hover']}
                  disabled={bulkLoading}
                >
                  <Button size="small" loading={bulkLoading}>
                    <ThunderboltOutlined /> Bulk Actions <CaretDownFilled />
                  </Button>
                </Dropdown>
              </Badge>
            )}
          </Space>
        </Col>
      </Row>

      <Table
        className="listings-list"
        childrenColumnName="__tableChildren"
        columns={parentColumns}
        dataSource={listings.map(listing => ({ ...listing, key: listing.id }))}
        loading={isLoading}
        rowClassName={(record, index) => getRowClassName(record, index, 'parent')}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          size: 'small',
          showQuickJumper: true,
          pageSizeOptions: ['10', '25', '50', '100'],
          showTotal: (total) => `Total ${total} listings`
        }}
        onChange={handleTableChange}
        rowSelection={{
          columnWidth: COLUMN_WIDTHS.selection,
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
          getCheckboxProps: (record) => ({
            disabled: record.status === 'DELETED'
          })
        }}
        tableLayout="fixed"
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange,
          onExpand: handleParentExpand,
          expandedRowRender: expandedParentRowRender,
          rowExpandable: isParentExpandable,
          showExpandColumn: false,
          expandRowByClick: false
        }}
      />

      <Modal
        title="Bulk Delete Listings / SKUs"
        open={bulkModalOpen}
        onCancel={() => setBulkModalOpen(false)}
        onOk={confirmBulkDelete}
        okText="Queue Amazon Delete"
        okButtonProps={{ danger: true, loading: bulkLoading }}
        confirmLoading={bulkLoading}
      >
        {(bulkPayload?.mode === 'filter' || bulkPreview?.mode === 'filter') && (
          <div style={{ marginBottom: 12 }}>
            <Text strong>Delete scope</Text>
            <Checkbox.Group
              style={{ display: 'block', marginTop: 8 }}
              value={filterBulkModes}
              onChange={handleFilterBulkModesChange}
              options={[
                { label: 'Listings matched by current filters', value: 'listing' },
                { label: 'Grouped SKUs matched by current filters', value: 'grouped_sku' }
              ]}
            />
          </div>
        )}
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Listings">{bulkPreview?.listingCount || 0}</Descriptions.Item>
          <Descriptions.Item label="Groups">{bulkPreview?.groupCount || 0}</Descriptions.Item>
          <Descriptions.Item label="SKU Items">{bulkPreview?.skuCount || 0}</Descriptions.Item>
          <Descriptions.Item label="Scope">{bulkPreview?.mode || bulkPayload?.mode || 'listing'}</Descriptions.Item>
          <Descriptions.Item label="Amazon delete">{bulkPreview?.willRequestAmazonDelete || bulkPreview?.skuCount || 0}</Descriptions.Item>
          <Descriptions.Item label="Local remove">0 records</Descriptions.Item>
        </Descriptions>
        <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
          This queues Amazon SP-API listing delete jobs and marks local SKU items as DELETED. Local records, order history, and grouping data are kept.
        </Text>
      </Modal>

      <Modal
        title={chartTitle}
        open={isChartModalVisible}
        onCancel={() => setIsChartModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Space>
            <Text type="secondary">Period</Text>
            <Select
              size="small"
              value={chartDays}
              style={{ width: 110 }}
              onChange={handleChartDaysChange}
              options={[7, 30, 90].map(value => ({ value, label: `${value} days` }))}
            />
          </Space>
          <Checkbox.Group
            value={chartVisibleMetrics}
            onChange={setChartVisibleMetrics}
            options={[
              { label: 'Sales', value: 'sales' },
              { label: 'Rank', value: 'rank' },
              { label: 'Price', value: 'price' }
            ]}
          />
        </Row>
        {chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis
                yAxisId="sales"
                hide={!chartVisibleMetrics.includes('sales')}
                width={68}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => Number(value).toLocaleString()}
                label={{
                  value: 'Sales (units)',
                  angle: -90,
                  position: 'insideLeft',
                  offset: 8,
                  style: { textAnchor: 'middle', fill: '#18a058', fontSize: 12, fontWeight: 600 }
                }}
              />
              <YAxis
                yAxisId="rank"
                hide={!chartVisibleMetrics.includes('rank')}
                orientation="right"
                reversed
                width={72}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `#${Number(value).toLocaleString()}`}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                hide={!chartVisibleMetrics.includes('price')}
                width={62}
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `$${value}`}
              />
              <RechartsTooltip
                formatter={(value, name) => {
                  if (value === null || value === undefined) return ['-', name];
                  if (name === 'rank') return [`#${Number(value).toLocaleString()}`, 'Rank'];
                  if (name === 'price') return [`$${Number(value).toFixed(2)}`, 'Price'];
                  return [`${value} units`, 'Sales'];
                }}
                labelFormatter={(label, payload) => {
                  if (payload && payload.length > 0) {
                    return payload[0].payload.fullDate;
                  }
                  return label;
                }}
              />
              <Legend />
              {chartVisibleMetrics.includes('sales') && (
                <Line yAxisId="sales" type="monotone" dataKey="sales" stroke="#18a058" strokeWidth={2} dot={{ r: 2 }} />
              )}
              {chartVisibleMetrics.includes('rank') && (
                <Line yAxisId="rank" type="monotone" dataKey="rank" stroke="#1890ff" strokeWidth={2} dot={{ r: 2 }} connectNulls />
              )}
              {chartVisibleMetrics.includes('price') && (
                <Line yAxisId="right" type="monotone" dataKey="price" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }} connectNulls />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Modal>
    </>
  );
};

export default ListingsList;
